import { DEFAULT_SITE, SITES, type SiteKey } from './sites';

export const DEFAULT_API_BASE = 'https://apiv2.vestiairecollective.com';

export interface VcApiOptions {
  site?: SiteKey;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

/**
 * Client for the Vestiaire public API. Adds the x-vc-* site params that every
 * extension used to append by hand. Called directly from the extension: the app
 * is served from a real origin, so there is no popup / postMessage detour.
 */
export function createVcApi(opts: VcApiOptions = {}) {
  const site = SITES[opts.site ?? DEFAULT_SITE];
  const base = (opts.baseUrl ?? DEFAULT_API_BASE).replace(/\/$/, '');
  const f = opts.fetchImpl ?? fetch;

  function url(path: string, params: Record<string, string> = {}): URL {
    const target = new URL(`${base}${path.startsWith('/') ? path : `/${path}`}`);
    target.searchParams.set('x-lang-code', site.langCode);
    target.searchParams.set('x-vc-language', site.language);
    target.searchParams.set('x-vc-currency', site.currency);
    target.searchParams.set('x-vc-siteid', site.siteId);
    for (const [k, v] of Object.entries(params)) target.searchParams.set(k, v);
    return target;
  }

  async function get<T>(path: string, params?: Record<string, string>): Promise<T> {
    let res: Response;
    try {
      res = await f(url(path, params));
    } catch (e) {
      // A TypeError from fetch is a network or CORS failure; the browser hides which. Say so.
      throw new Error(`Cannot reach ${base} from ${window.location.origin}: network error or origin not allowed by CORS (${String(e)})`);
    }
    if (!res.ok) throw new Error(`GET ${path} failed: ${res.status} ${res.statusText}`);
    return (await res.json()) as T;
  }

  return { url, get, site };
}

export type VcApi = ReturnType<typeof createVcApi>;
